// Camada de importação real do histórico dos WhatsApps (Normal + Business).
// Faz o parsing (CSV/XLSX), o auto-mapeamento de colunas para campos canônicos
// e a normalização de cada linha — sem gravar nada fictício.

import * as XLSX from 'xlsx';

export interface CampoDestino {
  key: string;
  label: string;
  obrigatorio?: boolean;
  hint?: string;
}

// Campos canônicos que o importador entende (mapeáveis para colunas do arquivo).
export const CAMPOS_DESTINO: CampoDestino[] = [
  { key: 'source_record_id', label: 'ID do registro (ContatoID)', obrigatorio: true, hint: 'Chave única do arquivo. Garante importação idempotente.' },
  { key: 'nome', label: 'Nome / Contato', obrigatorio: true },
  { key: 'telefone', label: 'Telefone / WhatsApp' },
  { key: 'empresa_nome', label: 'Empresa (nome)', hint: 'Só cria/víncula empresa quando houver nome explícito.' },
  { key: 'cnpj', label: 'CNPJ' },
  { key: 'cidade', label: 'Cidade', hint: 'Ex.: CidadeProvavel (inferida — tratada como estimativa).' },
  { key: 'uf', label: 'UF' },
  { key: 'score_lead', label: 'Score Lead' },
  { key: 'score_reativacao', label: 'Score Reativação' },
  { key: 'score_fornecedor', label: 'Score Fornecedor' },
  { key: 'score_arquiteto', label: 'Score Arquiteto' },
  { key: 'score_clinica', label: 'Score Clínica' },
  { key: 'score_final', label: 'Score Final (legado)' },
  { key: 'classificacao', label: 'Classificação (legado)' },
  { key: 'acao', label: 'Ação recomendada' },
  { key: 'dias_sem_contato', label: 'Dias sem contato' },
  { key: 'primeira', label: 'Primeira interação', hint: 'Data da primeira conversa.' },
  { key: 'ultima', label: 'Última interação', hint: 'Data da última conversa.' },
  { key: 'ultima_mensagem', label: 'Última mensagem (snapshot)' },
  { key: 'ultima_direcao', label: 'Última direção (recebida/enviada)' },
  { key: 'ticket', label: 'Potencial / Ticket' },
  { key: 'mensagens', label: 'Mensagens (total)' },
  { key: 'recebidas', label: 'Mensagens recebidas' },
  { key: 'enviadas', label: 'Mensagens enviadas' },
  { key: 'orcamentos', label: 'Qtd. Orçamento' },
  { key: 'projetos', label: 'Qtd. Projeto' },
  { key: 'quantidade', label: 'Qtd. Quantidade' },
  { key: 'preco', label: 'Qtd. Preço' },
  { key: 'desconto', label: 'Qtd. Desconto' },
  { key: 'cnpj_sinal', label: 'Qtd. CNPJ / NF' },
  { key: 'alto_valor', label: 'Sinais de alto valor' },
];

// Colunas de produto (viram produtos_interesse com intensidade de menções).
export const CAMPOS_PRODUTO: { key: string; label: string }[] = [
  { key: 'cadeiras', label: 'Cadeiras' },
  { key: 'mesas', label: 'Mesas / Estações' },
  { key: 'armarios', label: 'Armários / Gaveteiros' },
  { key: 'sofas', label: 'Sofás / Recepção' },
  { key: 'divisorias', label: 'Divisórias' },
  { key: 'carpete', label: 'Carpete / Pisos' },
  { key: 'acustica', label: 'Acústica' },
  { key: 'persianas', label: 'Persianas' },
  { key: 'marcenaria', label: 'Marcenaria' },
];

const ALIASES: Record<string, string[]> = {
  source_record_id: ['contatoid', 'id', 'idcontato', 'recordid', 'codigo', 'cod'],
  nome: ['nome', 'contato', 'name', 'cliente', 'contatonome'],
  telefone: ['telefone', 'whatsapp', 'numero', 'telefonenormalizado', 'celular', 'fone', 'phone'],
  empresa_nome: ['empresa', 'empresanome', 'organizacao', 'company', 'razaosocial', 'nomefantasia', 'empresanomefantasia'],
  cnpj: ['cnpj'],
  cidade: ['cidade', 'cidadeprovavel', 'city', 'municipio'],
  uf: ['uf', 'estado'],
  score_lead: ['scorelead'],
  score_reativacao: ['scorereativacao'],
  score_fornecedor: ['scorefornecedor'],
  score_arquiteto: ['scorearquiteto'],
  score_clinica: ['scoreclinica'],
  score_final: ['scorefinal', 'score'],
  classificacao: ['classificacao', 'classificacaolegacy', 'tiporelacionamento'],
  acao: ['acaorecomendada', 'acao', 'recomendacao'],
  dias_sem_contato: ['diassemcontato', 'diassemcontatoativo'],
  primeira: ['primeira', 'primeirainteracao', 'dataprimeira'],
  ultima: ['ultima', 'ultimainteracao', 'dataultima'],
  ultima_mensagem: ['ultimamensagem'],
  ultima_direcao: ['ultimadirecao'],
  ticket: ['potencialticket', 'ticket', 'potencial', 'valorpotencial'],
  mensagens: ['mensagens', 'totalmensagens', 'qtdmensagens'],
  recebidas: ['recebidas', 'mensagensrecebidas'],
  enviadas: ['enviadas', 'mensagensenviadas'],
  orcamentos: ['qtdorcamento', 'orcamentos', 'qtdorcamentos'],
  projetos: ['qtdprojeto', 'projetos', 'qtdprojetos'],
  quantidade: ['qtdquantidade', 'quantidade'],
  preco: ['qtdpreco', 'preco'],
  desconto: ['qtddesconto', 'desconto'],
  cnpj_sinal: ['qtdcnpj', 'notafiscal'],
  alto_valor: ['sinaisaltovalor', 'altovalor'],
  cadeiras: ['cadeiras', 'cadeira'],
  mesas: ['mesas', 'mesa', 'estacoes', 'estacao'],
  armarios: ['armarios', 'armario', 'gaveteiros'],
  sofas: ['sofas', 'sofasrecepcao', 'sofarecepcao', 'recepcao'],
  divisorias: ['divisorias', 'divisoria'],
  carpete: ['carpetepiso', 'carpete', 'pisos', 'piso'],
  acustica: ['acustica'],
  persianas: ['persianas', 'persiana'],
  marcenaria: ['marcenaria'],
};

function normHeader(h: string): string {
  return String(h ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '');
}

// Descobre a melhor coluna do arquivo para cada campo canônico.
export function autoMapear(headers: string[]): Record<string, string> {
  const mapa: Record<string, string> = {};
  const normHeaders = headers.map((h) => ({ original: h, n: normHeader(h) }));
  const usados = new Set<string>();

  const tentar = (key: string, aliases: string[]) => {
    for (const alias of aliases) {
      const found = normHeaders.find((h) => h.n === alias && !usados.has(h.original));
      if (found) { mapa[key] = found.original; usados.add(found.original); return; }
    }
  };

  for (const campo of CAMPOS_DESTINO) tentar(campo.key, ALIASES[campo.key] ?? []);
  for (const campo of CAMPOS_PRODUTO) tentar(campo.key, ALIASES[campo.key] ?? []);
  return mapa;
}

// Parser CSV robusto (aspas, vírgula ou tabulação).
export function parseCSV(texto: string): { headers: string[]; rows: Record<string, string>[] } {
  const clean = texto.replace(/^\uFEFF/, '');
  const primeiraLinha = clean.split(/\r?\n/)[0] ?? '';
  const tabs = (primeiraLinha.match(/\t/g) ?? []).length;
  const commas = (primeiraLinha.match(/,/g) ?? []).length;
  const delim = tabs > commas ? '\t' : ',';

  const registros: string[][] = [];
  let campo = '';
  let linha: string[] = [];
  let dentroAspas = false;
  for (let i = 0; i < clean.length; i += 1) {
    const c = clean[i];
    if (dentroAspas) {
      if (c === '"') {
        if (clean[i + 1] === '"') { campo += '"'; i += 1; } else { dentroAspas = false; }
      } else campo += c;
    } else if (c === '"') dentroAspas = true;
    else if (c === delim) { linha.push(campo); campo = ''; }
    else if (c === '\n') { linha.push(campo); registros.push(linha); linha = []; campo = ''; }
    else if (c === '\r') { /* ignora */ }
    else campo += c;
  }
  if (campo.length > 0 || linha.length > 0) { linha.push(campo); registros.push(linha); }

  const validas = registros.filter((r) => r.some((v) => String(v).trim().length > 0));
  if (validas.length === 0) return { headers: [], rows: [] };

  const headers = validas[0].map((h, i) => (String(h).trim() || `coluna_${i + 1}`));
  const rows = validas.slice(1).map((r) => {
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => { obj[h] = r[i] != null ? String(r[i]) : ''; });
    return obj;
  });
  return { headers, rows };
}

// Lê uma planilha XLSX (escolhe a aba com mais linhas, se não informada).
export function parseXLSX(data: ArrayBuffer, aba?: string): { sheets: string[]; headers: string[]; rows: Record<string, string>[] } {
  const wb = XLSX.read(data, { type: 'array' });
  const sheets = wb.SheetNames;
  const escolhida = aba && sheets.includes(aba)
    ? aba
    : sheets.reduce((best, s) => {
        const r = XLSX.utils.sheet_to_json(wb.Sheets[s], { header: 1 });
        const rb = XLSX.utils.sheet_to_json(wb.Sheets[best], { header: 1 });
        return (r?.length ?? 0) > (rb?.length ?? 0) ? s : best;
      }, sheets[0]);

  const matriz = XLSX.utils.sheet_to_json(wb.Sheets[escolhida], { header: 1, defval: '', raw: false }) as any[][];
  const validas = matriz.filter((r) => Array.isArray(r) && r.some((v) => String(v ?? '').trim().length > 0));
  if (validas.length === 0) return { sheets, headers: [], rows: [] };
  const headers = validas[0].map((h: any, i: number) => (String(h ?? '').trim() || `coluna_${i + 1}`));
  const rows = validas.slice(1).map((r) => {
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => { obj[h] = r[i] != null ? String(r[i]) : ''; });
    return obj;
  });
  return { sheets, headers, rows };
}

function derivarTipo(classificacao: string | null): string[] | null {
  const c = normHeader(classificacao ?? '');
  if (!c) return null;
  if (c.includes('arquitet')) return ['arquiteto_especificador'];
  if (c.includes('designer') || c.includes('especificador')) return ['designer_especificador'];
  if (c.includes('fornec') || c.includes('fabric') || c.includes('distribu') || c.includes('importad') || c.includes('revend')) return ['fornecedor_fabrica'];
  if (c.includes('construtora') || c.includes('incorporad')) return ['construtora_incorporadora'];
  if (c.includes('prefeitura') || c.includes('municipal') || c.includes('orgao') || c.includes('public') || c.includes('licita') || c.includes('secretaria')) return ['orgao_publico'];
  if (c.includes('clinica') || c.includes('saude') || c.includes('laborat') || c.includes('hospital')) return ['cliente_b2b'];
  if (c.includes('parceiro')) return ['parceiro'];
  if (c.includes('naoprospect') || c.includes('nao prospect')) return ['nao_prospectar'];
  return ['cliente_b2b'];
}

function num(v: string): number | null {
  if (v == null || String(v).trim() === '') return null;
  const n = Number(String(v).replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

// Converte uma linha bruta em registro canônico de staging.
export function rowToCanonical(row: Record<string, string>, mapping: Record<string, string>, origem: string, index: number) {
  const get = (key: string): string => { const h = mapping[key]; return h ? String(row[h] ?? '') : ''; };

  const produtos = CAMPOS_PRODUTO
    .map((p) => ({ produto: p.key, mencoes: num(get(p.key)) ?? 0 }))
    .filter((p) => p.mencoes > 0);

  const altoValorRaw = get('alto_valor').trim();
  const sinais = {
    mensagens: num(get('mensagens')),
    recebidas: num(get('recebidas')),
    enviadas: num(get('enviadas')),
    orcamentos: num(get('orcamentos')),
    projetos: num(get('projetos')),
    quantidade: num(get('quantidade')),
    preco: num(get('preco')),
    desconto: num(get('desconto')),
    cnpj: num(get('cnpj_sinal')),
    alto_valor: num(get('alto_valor')) ?? (altoValorRaw || null),
  };

  const sourceId = get('source_record_id').trim() || `linha-${index + 1}`;
  const classificacao = get('classificacao').trim() || null;

  const canonical = {
    source_record_id: sourceId,
    nome: get('nome').trim() || null,
    telefone: get('telefone').trim() || null,
    empresa_nome: get('empresa_nome').trim() || null,
    cnpj: get('cnpj').trim() || null,
    cidade: get('cidade').trim() || null,
    uf: get('uf').trim().toUpperCase() || null,
    whatsapp_origem: origem,
    score_lead: num(get('score_lead')),
    score_reativacao: num(get('score_reativacao')),
    score_fornecedor: num(get('score_fornecedor')),
    score_arquiteto: num(get('score_arquiteto')),
    score_clinica: num(get('score_clinica')),
    score_final: num(get('score_final')),
    classificacao,
    acao: get('acao').trim() || null,
    dias_sem_contato: num(get('dias_sem_contato')),
    primeira: get('primeira').trim() || null,
    ultima: get('ultima').trim() || null,
    ultima_mensagem: get('ultima_mensagem').trim().slice(0, 2000) || null,
    ultima_direcao: get('ultima_direcao').trim() || null,
    ticket: num(get('ticket')),
    produtos,
    sinais,
    tipo_relacionamento: derivarTipo(classificacao),
  };

  return { ...canonical, dado_bruto: canonical };
}

// Hash simples para detectar reenvio do mesmo arquivo.
export function hashTexto(texto: string): string {
  let h = 5381;
  const s = texto.slice(0, 100000);
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return `h${(h >>> 0).toString(16)}-${texto.length}`;
}